/**
 * AsHovercardWrapper Component
 * 
 * A wrapper component that manages the display of a hovercard attached to its child elements.
 * The hovercard content is provided as a child as-hovercard element and is shown on hover by default.
 * Uses @floating-ui/dom for robust positioning with collision detection and boundary awareness.
 * Uses Shadow DOM to protect positioning logic from CSS interference while keeping content styleable.
 * 
 * Usage:
 * ```html
 * <as-hovercard-wrapper>
 *   <some-element></some-element>
 *   <as-hovercard><img src="preview.jpg" alt="Preview"/></as-hovercard>
 * </as-hovercard-wrapper>
 * ```
 * 
 * Attributes:
 * - show: When present, displays the hovercard manually
 * - placement: Position of the hovercard (top, bottom, left, right, top-left, top-right, bottom-left, bottom-right)
 * - show-on-hover: When present, shows hovercard on hover (default behavior)
 * - toggle-on-click: When present, toggles hovercard visibility on click
 * - keep-open: When present, prevents hiding on pointer leave (hover mode) or outside clicks (click mode)
 * - delay: Delay in milliseconds before showing hovercard on hover (default: 200)
 * - hide-delay: Delay in milliseconds before hiding hovercard (default: 100)
 * - offset: Distance in pixels from the trigger element (default: 8)
 * 
 * Events:
 * - as-hovercard-show: Dispatched when hovercard is shown
 * - as-hovercard-hide: Dispatched when hovercard is hidden
 * - as-hovercard-close (listened): Closes the hovercard when received from child hovercard
 */

import { computePosition, flip, shift, offset } from '@floating-ui/dom';
import styleText from './as-hovercard-wrapper.css?raw';

export class AsHovercardWrapper extends HTMLElement {
  static get observedAttributes() {
    return ['show', 'placement', 'show-on-hover', 'toggle-on-click', 'keep-open', 'delay', 'hide-delay', 'offset'];
  }

  private showTimeout: number | null = null;
  private hideTimeout: number | null = null;
  private isHovering = false;
  private outsideClickHandler: ((event: Event) => void) | null = null;
  private hovercard: HTMLElement | null = null;
  private triggerSlot: HTMLSlotElement;
  private hovercardSlot: HTMLSlotElement;
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsHovercardWrapper.sheet
    ];
    this.setupShadowDOM();
    this.triggerSlot = this.shadowRoot!.querySelector('slot[name="trigger"]') as HTMLSlotElement;
    this.hovercardSlot = this.shadowRoot!.querySelector('slot[name="hovercard"]') as HTMLSlotElement;
  }

  /**
   * Setup Shadow DOM structure and styles
   */
  private setupShadowDOM(): void {
    this.shadowRoot!.innerHTML = `
      <div class="wrapper">
        <slot name="trigger"></slot>
        <div class="hovercard-container">
          <slot name="hovercard"></slot>
        </div>
      </div>
    `;
  }

  /**
   * Get configuration values with defaults
   */
  private get config() {
    return {
      delay: parseInt(this.getAttribute('delay') ?? '200'),
      hideDelay: parseInt(this.getAttribute('hide-delay') ?? '100'),
      offset: parseInt(this.getAttribute('offset') ?? '8'),
      placement: this.getAttribute('placement') || 'bottom',
      showOnHover: this.getAttribute('show-on-hover') !== 'false',
      toggleOnClick: this.hasAttribute('toggle-on-click'),
      keepOpen: this.hasAttribute('keep-open')
    };
  }

  /**
   * Get the hovercard element and cache it
   */
  private getHovercard(): HTMLElement | null {
    if (!this.hovercard) {
      this.hovercard = this.querySelector('as-hovercard') as HTMLElement;
    }
    return this.hovercard;
  }

  /**
   * Get the hovercard container in shadow DOM
   */
  private getHovercardContainer(): HTMLElement {
    return this.shadowRoot!.querySelector('.hovercard-container') as HTMLElement;
  }

  /**
   * Map placement terms to Floating UI placements
   */
  private mapPlacement(placement: string): string {
    const placementMap: { [key: string]: string } = {
      'top': 'top', 'bottom': 'bottom', 'left': 'left', 'right': 'right',
      'top-left': 'top-end', 'top-right': 'top-start',
      'bottom-left': 'bottom-end', 'bottom-right': 'bottom-start'
    };
    return placementMap[placement] || 'bottom';
  }

  /**
   * Clear all active timeouts
   */
  private clearTimeouts(): void {
    if (this.showTimeout) clearTimeout(this.showTimeout);
    if (this.hideTimeout) clearTimeout(this.hideTimeout);
    this.showTimeout = this.hideTimeout = null;
  }

  /**
   * Show the hovercard with positioning
   */
  private async showHovercard(): Promise<void> {
    const hovercard = this.getHovercard();
    const container = this.getHovercardContainer();
    if (!hovercard || !container) return;

    this.clearTimeouts();
    
    // Assign slots before positioning
    this.assignSlots();
    await this.updatePosition();
    
    container.setAttribute('data-show', '');
    this.setAttribute('data-active', '');
    
    this.setupOutsideClickListener();
    this.dispatchEvent(new CustomEvent('as-hovercard-show', { bubbles: true, composed: true }));
  }

  /**
   * Hide the hovercard
   */
  private hideHovercard(): void {
    const container = this.getHovercardContainer();
    if (!container) return;

    this.clearTimeouts();
    container.removeAttribute('data-show');
    this.removeAttribute('data-active');
    
    this.removeOutsideClickListener();
    this.dispatchEvent(new CustomEvent('as-hovercard-hide', { bubbles: true, composed: true }));
  }

  /**
   * Assign elements to appropriate slots
   */
  private assignSlots(): void {
    const children = Array.from(this.children);
    
    children.forEach(child => {
      if (child.tagName.toLowerCase() === 'as-hovercard') {
        child.setAttribute('slot', 'hovercard');
      } else {
        child.setAttribute('slot', 'trigger');
      }
    });
  }

  /**
   * Update hovercard position using Floating UI
   */
  private async updatePosition(): Promise<void> {
    const container = this.getHovercardContainer();
    if (!container) return;

    const { placement, offset: offsetValue } = this.config;

    try {
      const { x, y } = await computePosition(this, container, {
        placement: this.mapPlacement(placement) as any,
        strategy: 'fixed', // Use fixed positioning strategy
        middleware: [offset(offsetValue), flip(), shift({ padding: 5 })],
      });

      Object.assign(container.style, {
        left: `${x}px`,
        top: `${y}px`,
        transform: 'none'
      });
    } catch (error) {
      console.warn('Failed to position hovercard:', error);
    }
  }

  /**
   * Unified event handler for all interactions
   */
  private handleInteraction = (event: Event): void => {
    const { showOnHover, toggleOnClick, keepOpen, delay, hideDelay } = this.config;
    
    switch (event.type) {
      case 'pointerenter':
        // Only handle hover if not in click mode
        if (showOnHover && !toggleOnClick) {
          this.isHovering = true;
          this.clearTimeouts();
          this.showTimeout = window.setTimeout(() => {
            if (this.isHovering) this.showHovercard();
          }, delay);
        }
        break;
        
      case 'pointerleave':
        // Only handle hover if not in click mode
        if (showOnHover && !toggleOnClick && !keepOpen) {
          this.isHovering = false;
          this.clearTimeouts();
          this.hideTimeout = window.setTimeout(() => {
            if (!this.isHovering) this.hideHovercard();
          }, hideDelay);
        }
        break;
        
      case 'click':
        if (toggleOnClick) {
          const container = this.getHovercardContainer();
          const isCurrentlyShowing = container?.hasAttribute('data-show');
          
          // Check if the click originated from inside the hovercard content
          const target = event.target as Node;
          const hovercard = this.getHovercard();
          const isClickFromHovercard = hovercard?.contains(target) ?? false;
          
          // Ignore clicks that originate from inside the hovercard
          if (isClickFromHovercard) {
            return;
          }
          
          if (isCurrentlyShowing) {
            this.hideHovercard();
          } else {
            this.showHovercard();
          }
        }
        break;
    }
  }

  /**
   * Handle outside clicks for click mode
   */
  private handleOutsideClick = (event: Event): void => {
    if (this.config.keepOpen) return;
    
    const target = event.target as Node;
    const hovercard = this.getHovercard();
    
    // Check if click is inside the wrapper (trigger element) or hovercard content
    const isInsideWrapper = this.contains(target);
    const isInsideHovercard = hovercard?.contains(target) ?? false;
    
    // Only close if the click is outside both the wrapper and hovercard
    if (!isInsideWrapper && !isInsideHovercard) {
      this.hideHovercard();
    }
  }

  /**
   * Setup outside click listener
   */
  private setupOutsideClickListener(): void {
    if (this.config.toggleOnClick && !this.outsideClickHandler) {
      this.outsideClickHandler = this.handleOutsideClick;
      // Use bubble phase (false) instead of capture phase to ensure proper event handling
      document.addEventListener('click', this.outsideClickHandler, false);
    }
  }

  /**
   * Remove outside click listener
   */
  private removeOutsideClickListener(): void {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler, false);
      this.outsideClickHandler = null;
    }
  }

  /**
   * Handle close events from hovercard
   */
  private handleCloseEvent = (event: Event): void => {
    event.stopPropagation();
    this.hideHovercard();
  }

  /**
   * Handle hovercard hover events
   */
  private handleHovercardHover = (event: Event): void => {
    // Only handle if not in click mode
    if (!this.config.toggleOnClick) {
      if (event.type === 'pointerenter') {
        this.isHovering = true;
        this.clearTimeouts();
      } else if (event.type === 'pointerleave' && !this.config.keepOpen) {
        this.isHovering = false;
        this.hideTimeout = window.setTimeout(() => {
          if (!this.isHovering) this.hideHovercard();
        }, this.config.hideDelay);
      }
    }
  }

  /**
   * Setup all event listeners
   */
  private setupEvents(): void {
    // Listen on the host element for interactions
    this.addEventListener('pointerenter', this.handleInteraction);
    this.addEventListener('pointerleave', this.handleInteraction);
    this.addEventListener('click', this.handleInteraction);
    this.addEventListener('as-hovercard-close', this.handleCloseEvent);
    
    // Listen for hovercard hover events on the slotted elements
    this.hovercardSlot.addEventListener('slotchange', () => {
      const hovercard = this.getHovercard();
      if (hovercard) {
        hovercard.addEventListener('pointerenter', this.handleHovercardHover);
        hovercard.addEventListener('pointerleave', this.handleHovercardHover);
      }
    });
  }

  /**
   * Cleanup all event listeners and timeouts
   */
  private cleanup(): void {
    this.clearTimeouts();
    this.removeOutsideClickListener();
    this.removeEventListener('pointerenter', this.handleInteraction);
    this.removeEventListener('pointerleave', this.handleInteraction);
    this.removeEventListener('click', this.handleInteraction);
    this.removeEventListener('as-hovercard-close', this.handleCloseEvent);
    
    // Clean up hovercard event listeners
    const hovercard = this.getHovercard();
    if (hovercard) {
      hovercard.removeEventListener('pointerenter', this.handleHovercardHover);
      hovercard.removeEventListener('pointerleave', this.handleHovercardHover);
    }
  }

  /**
   * Handle attribute changes without full re-render
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (!this.isConnected || oldValue === newValue) return;

    switch (name) {
      case 'show':
        if (newValue !== null) {
          this.showHovercard();
        } else {
          this.hideHovercard();
        }
        break;
        
      case 'placement':
      case 'offset':
        // Update position if currently visible
        const container = this.getHovercardContainer();
        if (container?.hasAttribute('data-show')) {
          this.updatePosition();
        }
        break;
    }
  }

  connectedCallback(): void {
    this.assignSlots();
    this.setupEvents();
    
    // Show immediately if show attribute is present
    if (this.hasAttribute('show')) {
      setTimeout(() => this.showHovercard(), 0);
    }
  }
  
  disconnectedCallback(): void {
    this.cleanup();
  }

  /**
   * Get child bounds for external positioning
   */
  get childBounds(): DOMRect {
    return this.getBoundingClientRect();
  }
}

customElements.define('as-hovercard-wrapper', AsHovercardWrapper);
